import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import {
  Loader2,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Clock,
  FileText,
  Building2,
  Users,
  Package,
  Calendar
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Base loading spinner component
interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  color?: 'primary' | 'secondary' | 'muted';
}

export function LoadingSpinner({ size = 'md', className, color = 'primary' }: LoadingSpinnerProps) {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-12 h-12'
  };

  const colorClasses = {
    primary: 'text-primary',
    secondary: 'text-secondary',
    muted: 'text-muted-foreground'
  };

  return (
    <Loader2
      className={cn(
        'animate-spin',
        sizeClasses[size],
        colorClasses[color],
        className
      )}
    />
  );
}

// Inline loading indicator for buttons
interface InlineLoadingProps {
  text?: string;
  size?: 'sm' | 'md';
  className?: string;
}

export function InlineLoading({ text = 'Loading...', size = 'sm', className }: InlineLoadingProps) {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <LoadingSpinner size={size} />
      {text && <span className="text-sm text-muted-foreground">{text}</span>}
    </div>
  );
}

// Page-level loading screen
interface PageLoadingProps {
  title?: string;
  description?: string;
  progress?: number;
  className?: string;
}

export function PageLoading({
  title = 'Loading...',
  description,
  progress,
  className
}: PageLoadingProps) {
  return (
    <div className={cn('min-h-screen flex items-center justify-center p-4', className)}>
      <Card className="w-full max-w-md">
        <CardContent className="p-8">
          <div className="text-center space-y-4">
            <LoadingSpinner size="xl" />
            <div>
              <h3 className="text-lg font-semibold">{title}</h3>
              {description && (
                <p className="text-sm text-muted-foreground mt-1">{description}</p>
              )}
            </div>
            {typeof progress === 'number' && (
              <div className="space-y-2">
                <Progress value={progress} className="w-full" />
                <p className="text-xs text-muted-foreground">{progress}% complete</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Card loading skeleton
interface CardLoadingProps {
  lines?: number;
  showHeader?: boolean;
  showActions?: boolean;
  className?: string;
}

export function CardLoading({
  lines = 3,
  showHeader = true,
  showActions = false,
  className
}: CardLoadingProps) {
  return (
    <Card className={className}>
      {showHeader && (
        <CardHeader>
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </CardHeader>
      )}
      <CardContent className="space-y-3">
        {Array.from({ length: lines }).map((_, i) => (
          <Skeleton key={i} className={`h-4 ${i === lines - 1 ? 'w-2/3' : 'w-full'}`} />
        ))}
        {showActions && (
          <div className="flex gap-2 pt-4">
            <Skeleton className="h-9 w-20" />
            <Skeleton className="h-9 w-16" />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Table loading skeleton
interface TableLoadingProps {
  rows?: number;
  columns?: number;
  showHeader?: boolean;
  className?: string;
}

export function TableLoading({
  rows = 5,
  columns = 4,
  showHeader = true,
  className
}: TableLoadingProps) {
  return (
    <Card className={className}>
      <CardContent className="p-0">
        <div className="space-y-0">
          {showHeader && (
            <div className="border-b p-4">
              <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
                {Array.from({ length: columns }).map((_, i) => (
                  <Skeleton key={i} className="h-4 w-full" />
                ))}
              </div>
            </div>
          )}
          <div className="divide-y">
            {Array.from({ length: rows }).map((_, i) => (
              <div key={i} className="p-4">
                <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
                  {Array.from({ length: columns }).map((_, j) => (
                    <Skeleton key={j} className="h-4 w-full" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Booking-specific loading skeletons
export function BookingListLoading({ count = 5 }: { count?: number }) {
  return (
    <div className="space-y-4">
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i} className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4 flex-1">
              <div className="w-10 h-10 bg-muted rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <div className="flex items-center gap-2">
                  <Skeleton className="h-6 w-20" />
                  <Skeleton className="h-6 w-16" />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-8 w-16" />
            </div>
          </div>
          <Separator className="my-4" />
          <div className="flex items-center justify-between">
            <Skeleton className="h-2 w-full max-w-sm" />
            <Skeleton className="h-8 w-20" />
          </div>
        </Card>
      ))}
    </div>
  );
}

export function BookingDetailLoading() {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Skeleton className="h-8 w-24" />
          <div className="space-y-2">
            <Skeleton className="h-7 w-80" />
            <Skeleton className="h-4 w-48" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Skeleton className="h-6 w-24" />
          <Skeleton className="h-6 w-32" />
        </div>
      </div>

      <Separator />

      {/* Tabs */}
      <div className="space-y-4">
        <div className="flex gap-2">
          <Skeleton className="h-9 w-20" />
          <Skeleton className="h-9 w-20" />
          <Skeleton className="h-9 w-20" />
        </div>

        {/* Tab content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <CardLoading lines={6} showHeader={true} />
            <CardLoading lines={4} showHeader={true} />
          </div>
          <div className="space-y-6">
            <CardLoading lines={8} showHeader={true} showActions={true} />
          </div>
        </div>
      </div>
    </div>
  );
}

// Error states with retry functionality
interface ErrorStateProps {
  title?: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  variant?: 'error' | 'warning' | 'info';
  className?: string;
}

export function ErrorState({
  title = 'Something went wrong',
  description = 'Please try again later.',
  action,
  variant = 'error',
  className
}: ErrorStateProps) {
  const variants = {
    error: {
      icon: AlertCircle,
      iconColor: 'text-red-600',
      bgColor: 'bg-red-100'
    },
    warning: {
      icon: AlertCircle,
      iconColor: 'text-yellow-600',
      bgColor: 'bg-yellow-100'
    },
    info: {
      icon: AlertCircle,
      iconColor: 'text-blue-600',
      bgColor: 'bg-blue-100'
    }
  };

  const config = variants[variant];
  const Icon = config.icon;

  return (
    <Card className={className}>
      <CardContent className="p-8 text-center">
        <div className={cn('w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4', config.bgColor)}>
          <Icon className={cn('w-8 h-8', config.iconColor)} />
        </div>
        <h3 className="text-lg font-semibold mb-2">{title}</h3>
        <p className="text-muted-foreground mb-6">{description}</p>
        {action && (
          <Button onClick={action.onClick} className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
            {action.label}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// Empty state component
interface EmptyStateProps {
  title: string;
  description?: string;
  icon?: React.ComponentType<{ className?: string }>;
  action?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
}

export function EmptyState({
  title,
  description,
  icon: Icon = FileText,
  action,
  className
}: EmptyStateProps) {
  return (
    <Card className={className}>
      <CardContent className="p-12 text-center">
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon className="w-8 h-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">{title}</h3>
        {description && (
          <p className="text-muted-foreground mb-6">{description}</p>
        )}
        {action && (
          <Button onClick={action.onClick} className="flex items-center gap-2">
            {action.label}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// Progressive loading component with phases
interface ProgressiveLoadingProps {
  phases: Array<{
    label: string;
    completed: boolean;
    error?: boolean;
  }>;
  currentPhase?: number;
  className?: string;
}

export function ProgressiveLoading({ phases, currentPhase = 0, className }: ProgressiveLoadingProps) {
  return (
    <Card className={className}>
      <CardContent className="p-6">
        <div className="space-y-4">
          <h3 className="font-medium">Loading...</h3>
          <div className="space-y-3">
            {phases.map((phase, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full border-2 flex items-center justify-center">
                  {phase.error ? (
                    <AlertCircle className="w-4 h-4 text-red-500" />
                  ) : phase.completed ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : index === currentPhase ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <Clock className="w-4 h-4 text-muted-foreground" />
                  )}
                </div>
                <span className={cn(
                  'text-sm',
                  phase.error ? 'text-red-600' :
                  phase.completed ? 'text-green-600' :
                  index === currentPhase ? 'text-foreground' :
                  'text-muted-foreground'
                )}>
                  {phase.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Async operation wrapper with loading states
interface AsyncWrapperProps<T> {
  children: (data: T) => React.ReactNode;
  loading?: boolean;
  error?: Error | string | null;
  data?: T;
  loadingComponent?: React.ReactNode;
  errorComponent?: React.ReactNode;
  emptyComponent?: React.ReactNode;
  onRetry?: () => void;
  isEmpty?: (data: T) => boolean;
}

export function AsyncWrapper<T>({
  children,
  loading = false,
  error,
  data,
  loadingComponent,
  errorComponent,
  emptyComponent,
  onRetry,
  isEmpty
}: AsyncWrapperProps<T>) {
  if (loading) {
    return <>{loadingComponent || <InlineLoading />}</>;
  }

  if (error) {
    if (errorComponent) {
      return <>{errorComponent}</>;
    }

    return (
      <ErrorState
        title="Failed to load data"
        description={typeof error === 'string' ? error : error.message}
        action={onRetry ? { label: 'Try Again', onClick: onRetry } : undefined}
      />
    );
  }

  if (!data || (isEmpty && isEmpty(data))) {
    return <>{emptyComponent || <EmptyState title="No data available" />}</>;
  }

  return <>{children(data)}</>;
}